import React, { useState, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import * as Popover from '@radix-ui/react-popover';
import { PanelLeftClose, PanelLeftOpen, Info, RotateCcw } from 'lucide-react';
import { useT } from '../i18n';
import { useHighlight } from '../hooks/useHighlight';
import { HELP_CONTENT } from './help/helpContent';

// --- localStorage helpers (never throw) ---

function readOpen(page) {
  try {
    const v = localStorage.getItem(`help.${page}.open`);
    if (v === null) {
      // T-118: en tablet/celular el panel robaria ancho al documento; en desktop
      // sigue abierto por defecto. Solo afecta el primer login (sin preferencia).
      try { return window.matchMedia('(min-width: 1024px)').matches; } catch { return true; }
    }
    return v === '1';
  } catch {
    return true;
  }
}

function writeOpen(page, open) {
  try {
    localStorage.setItem(`help.${page}.open`, open ? '1' : '0');
  } catch { /* ignore */ }
}

function readProgress(page) {
  try {
    const raw = localStorage.getItem(`help.${page}.progress`);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch {
    return {};
  }
}

function writeProgress(page, progress) {
  try {
    localStorage.setItem(`help.${page}.progress`, JSON.stringify(progress));
  } catch { /* ignore */ }
}

// --- Component ---

export default function HelpPanel({ page }) {
  const t = useT();
  const { highlight, clear } = useHighlight();

  // Estado abierto/cerrado, persistente por página.
  const [open, setOpen] = useState(() => readOpen(page));

  // Progreso del checklist: { [tabId]: { [stepId]: true } }
  const [progress, setProgress] = useState(() => readProgress(page));

  // Tab activo (default: primer tab de la página).
  const pageContent = HELP_CONTENT[page];
  const tabs = pageContent?.tabs ?? [];
  const [activeTab, setActiveTab] = useState(() => tabs[0]?.id ?? '');

  // --- Handlers ---

  const toggleOpen = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      writeOpen(page, next);
      return next;
    });
  }, [page]);

  const isDone = useCallback((tabId, stepId) => {
    return !!(progress[tabId] && progress[tabId][stepId]);
  }, [progress]);

  const toggle = useCallback((tabId, stepId) => {
    setProgress((prev) => {
      const tabProg = { ...(prev[tabId] || {}) };
      if (tabProg[stepId]) {
        delete tabProg[stepId];
      } else {
        tabProg[stepId] = true;
      }
      const next = { ...prev, [tabId]: tabProg };
      writeProgress(page, next);
      return next;
    });
  }, [page]);

  const resetTab = useCallback((tabId) => {
    setProgress((prev) => {
      const next = { ...prev };
      delete next[tabId];
      writeProgress(page, next);
      return next;
    });
  }, [page]);

  // --- Collapsed (closed) rendering ---

  if (!open) {
    return (
      <div className="w-10 shrink-0 h-screen sticky top-0 border-r border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/60 backdrop-blur flex flex-col items-center justify-start py-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleOpen}
          title={t('help.open')}
        >
          <PanelLeftOpen className="w-5 h-5" />
        </Button>
        <span
          className="mt-2 text-xs text-slate-500 dark:text-slate-400 select-none"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          {t('help.title')}
        </span>
      </div>
    );
  }

  // --- Open (expanded) rendering ---

  return (
    <aside className="w-80 shrink-0 h-screen sticky top-0 border-r border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/60 backdrop-blur flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <span className="font-semibold text-slate-800 dark:text-slate-100">
          {t('help.title')}
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleOpen}
          title={t('help.close')}
        >
          <PanelLeftClose className="w-5 h-5" />
        </Button>
      </div>

      <p className="px-3 pb-2 text-xs text-slate-500 dark:text-slate-400">
        {t('help.hoverHint')}
      </p>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
        <TabsList className="flex flex-wrap h-auto p-1 gap-1 text-xs">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="text-xs px-2 py-1">
              {t(tab.titleKey)}
            </TabsTrigger>
          ))}
        </TabsList>

        {tabs.map((tab) => {
          const total = tab.checklist.length;
          const done = tab.checklist.filter((s) => isDone(tab.id, s.id)).length;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;

          return (
            <TabsContent key={tab.id} value={tab.id} className="flex-1 min-h-0 mt-0">
              <ScrollArea className="flex-1 px-3 pb-4 h-full">
                {/* Tutorial section */}
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-2 mb-2">
                  {t('help.tutorial')}
                </h3>

                {tab.checklist.map((step) => {
                  const checked = isDone(tab.id, step.id);
                  return (
                    <div key={step.id} className="flex gap-2 items-start py-1">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggle(tab.id, step.id)}
                        className="mt-0.5"
                      />
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => { if (step.target) highlight(step.target); }}
                        onMouseEnter={() => { if (step.target) highlight(step.target, 0); }}
                        onMouseLeave={clear}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && step.target) {
                            e.preventDefault();
                            highlight(step.target);
                          }
                        }}
                        className={`text-sm cursor-pointer select-none ${checked
                          ? 'line-through text-slate-400 dark:text-slate-500'
                          : 'text-slate-700 dark:text-slate-200'
                        }`}
                      >
                        {t(step.key)}
                      </span>
                    </div>
                  );
                })}

                {/* Progress bar */}
                <div className="mt-3">
                  <div className="rounded bg-slate-200 dark:bg-slate-700 h-2 overflow-hidden">
                    <div
                      className="h-full bg-sky-500 transition-all duration-300"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {done} {t('help.progress_of')} {total}
                  </p>
                </div>

                {/* Reset button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resetTab(tab.id)}
                  className="mt-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  {t('help.reset')}
                </Button>

                <Separator className="my-3" />

                {/* Reference section */}
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                  {t('help.reference')}
                </h3>

                {tab.refs.map((ref) => (
                  <div
                    key={ref.id}
                    className="flex items-center justify-between gap-2 py-1"
                  >
                    <span
                      role="button"
                      tabIndex={ref.target ? 0 : undefined}
                      onClick={() => { if (ref.target) highlight(ref.target); }}
                      onMouseEnter={() => { if (ref.target) highlight(ref.target, 0); }}
                      onMouseLeave={clear}
                      onKeyDown={(e) => {
                        if (ref.target && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          highlight(ref.target);
                        }
                      }}
                      className={`text-sm ${ref.target
                        ? 'cursor-pointer text-slate-700 dark:text-slate-200 hover:text-sky-500'
                        : 'text-slate-700 dark:text-slate-200'
                      }`}
                    >
                      {t(ref.key)}
                    </span>

                    {ref.detailKey && (
                      <Popover.Root>
                        <Popover.Trigger asChild>
                          <button
                            title={t('help.more')}
                            className="p-1 text-slate-500 hover:text-sky-500"
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        </Popover.Trigger>
                        <Popover.Portal>
                          <Popover.Content
                            sideOffset={6}
                            className="z-50 max-w-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 text-sm shadow-lg"
                          >
                            {t(ref.detailKey)}
                          </Popover.Content>
                        </Popover.Portal>
                      </Popover.Root>
                    )}
                  </div>
                ))}
              </ScrollArea>
            </TabsContent>
          );
        })}
      </Tabs>
    </aside>
  );
}
